import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import config from 'config';
import BackButton from 'components/BackButton';
import { Focusable, FocusableParent } from 'components/navigation';
import HorizontalList from 'components/HorizontalList';

interface IHeaderProps {
  imageUrl?: string;
}

const FAST_TRANSITION = '0.3s';

export const DetailsWrapper = styled.div`
  padding-left: ${scaleSize(220)}px;
  height: ${scaleSize(1080)}px;
`;

export const Header = styled.div.attrs<IHeaderProps>(({ imageUrl }) => ({
  style: {
    background: imageUrl ? `url(${imageUrl}) no-repeat center bottom / contain` : '',
  },
}))<IHeaderProps>`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(1080)}px;
  background: ${({ theme }) => theme.imagePlaceholder}
    linear-gradient(to top, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0));
  margin-left: -${scaleSize(220)}px;
  position: relative;
`;

export const StyledImageOverlay = styled.div`
  position: absolute;
  top: ${scaleSize(722)}px;
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(360)}px;
  background: linear-gradient(
    360deg,
    ${({ theme }) => theme.backgroundColor} 0.09%,
    rgba(38, 38, 38, 0.0001) 100%
  );
`;

interface IDetailsInnerWrapper {
  pageOffset: number;
}

export const DetailsInnerWrapper = styled.div.attrs<IDetailsInnerWrapper>(({ pageOffset }) => ({
  style: {
    transfrom: `translate3d(0, ${scaleSize(pageOffset)}px, 0)`,
  },
}))<IDetailsInnerWrapper>`
  width: ${scaleSize(1920)}px;
  transform: translate3d(0, ${({ pageOffset }) => scaleSize(pageOffset)}px, 0);
  position: relative;
`;
export const StyledBackButton = styled(BackButton)`
  z-index: 4;
  left: -${scaleSize(186)}px;
  color: ${({ theme, buttonOffset }) =>
    buttonOffset > 540 ? theme.activeColor : theme.detailsTitleTextColor};
  transform: translate3d(0, ${({ buttonOffset }) => scaleSize(buttonOffset)}px, 0);
`;

export const StyledAvailableStatus = styled.div`
  position: absolute;
  top: ${scaleSize(650)}px;
  width: ${scaleSize(432)}px;
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  font-family: 'RobotoMedium', sans-serif;
  font-weight: 500;
  text-align: center;
  color: ${({ theme }) => theme.activeColor};
`;

interface ICoverWrapper {
  opacity: number;
  coverOffset: number;
}

export const CoverWrapper = styled(FocusableParent)<ICoverWrapper>`
  width: ${scaleSize(432)}px;
  height: ${scaleSize(640)}px;
  position: absolute;
  top: ${scaleSize(760)}px;
  right: ${scaleSize(120)}px;
  opacity: ${({ opacity }) => opacity};
  transform: translate3d(0, ${({ coverOffset }) => scaleSize(coverOffset)}px, 0);
`;

interface ICover {
  cover: string;
}

export const Cover = styled(Focusable)<ICover>`
  display: inline-block;
  width: ${scaleSize(432)}px;
  height: ${scaleSize(640)}px;
  position: relative;
  box-shadow: 0 15px 40px 0 rgba(0, 0, 0, 0.15);
  background-color: ${({ theme }) => theme.mainTextColor};
  background-image: url(${({ cover }) => cover});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-sizing: border-box;
  border-radius: ${scaleSize(16)}px;

  > svg {
    position: absolute;
    bottom: ${scaleSize(20)}px;
    right: ${scaleSize(20)}px;
    opacity: 0;
    width: ${scaleSize(100)}px;
    height: ${scaleSize(100)}px;
  }

  &:after {
    content: '';
    display: block;
    width: ${scaleSize(432 - 15)}px;
    height: ${scaleSize(630)}px;
    position: absolute;
    top: 0;
    border: solid ${scaleSize(8)}px transparent;
    border-radius: ${scaleSize(16)}px;
  }

  &:focus {
    &:after {
      border-color: ${({ theme }) => theme.activeColor};
    }
    > svg {
      opacity: 1;
    }
  }
`;

interface ISecondaryHeader {
  isVisible: boolean;
  headerOffset: number;
}

export const SecondaryHeader = styled.div<ISecondaryHeader>`
  width: ${scaleSize(1100)}px;
  height: ${scaleSize(124)}px;
  position: absolute;
  top: ${scaleSize(1113)}px;
  left: 0;
  z-index: 1;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition-delay: opacity ${({ isVisible }) => (isVisible ? FAST_TRANSITION : '0s')};
  transform: translate3d(0, ${({ headerOffset }) => scaleSize(headerOffset)}px, 0);
  background: ${({ theme }) =>
    `linear-gradient(transparent 0%, ${theme.backgroundColor} 20%, ${theme.backgroundColor} 80%, transparent 100%)`};

  & > div:nth-child(1) {
    font-size: ${scaleSize(48)}px;
    line-height: ${scaleSize(72)}px;
    color: ${({ theme }) => theme.mainTextColor};
    position: relative;
    top: 0;
    left: 0;
  }

  & > div:nth-child(2) {
    font-size: ${scaleSize(34)}px;
    line-height: ${scaleSize(48)}px;
    color: ${({ theme }) => theme.secondaryTextColor};
    position: relative;
    top: 0;
    left: 0;
  }
`;

interface IActionButtonsWrapper {
  isVisible: boolean;
}

export const ActionButtonsWrapper = styled(FocusableParent)<IActionButtonsWrapper>`
  width: ${scaleSize(1048)}px;
  height: ${scaleSize(80)}px;
  margin-top: ${scaleSize(60)}px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: ${config.withAnimation ? `opacity ${FAST_TRANSITION}` : 'none'};
  transition-delay: ${({ isVisible }) => (isVisible ? FAST_TRANSITION : '0s')};
`;

export const ExpandTextWrapper = styled(FocusableParent)`
  width: ${scaleSize(1048)}px;
  height: ${scaleSize(80)}px;
  margin-top: ${scaleSize(60)}px;
`;

export const DescriptionWrapper = styled.div`
  width: ${scaleSize(1048)}px;
  margin-top: ${scaleSize(60)}px;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  font-family: 'RobotoRegular', sans-serif;
`;

interface IDescriptionCastWrapper {
  isVisible: boolean;
}

export const DescriptionCastWrapper = styled.div<IDescriptionCastWrapper>`
  height: ${({ isVisible }) => (isVisible ? `${scaleSize(500)}px` : `${scaleSize(250)}px`)};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: ${config.withAnimation ? `opacity ${FAST_TRANSITION}` : 'none'};
`;

export const CastWrapper = styled.div`
  width: ${scaleSize(1048)}px;
  margin-top: ${scaleSize(40)}px;
`;

export const CastTitle = styled.div`
  width: ${scaleSize(160)}px;
  display: inline-block;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  vertical-align: top;
`;

export const CastList = styled.div`
  width: ${scaleSize(888)}px;
  display: inline-block;
  color: ${({ theme }) => theme.mainTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
`;

export const SeasonsWrapper = styled.div`
  width: ${scaleSize(1480)}px;
  margin-top: ${scaleSize(100)}px;
`;

export const SeasonsTabs = styled(HorizontalList)`
  width: ${scaleSize(1480)}px;
  height: ${scaleSize(80)}px;
  margin-bottom: ${scaleSize(40)}px;
  border-bottom: solid 2px #444444;
  white-space: nowrap;
`;

interface ISeasonsTab {
  isSelected: boolean;
  itemWidth: number;
}

export const SeasonsTab = styled(Focusable)<ISeasonsTab>`
  font-family: 'Roboto-Medium', sans-serif;
  font-size: ${scaleSize(30)}px;
  line-height: ${scaleSize(80)}px;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 0 ${scaleSize(30)}px;
  color: ${({ theme }) => theme.mainTextColor};
  display: inline-block;
  border-top-left-radius: ${scaleSize(12)}px;
  border-top-right-radius: ${scaleSize(12)}px;
  background: ${({ isSelected }) => (isSelected ? '#444444' : 'transparent')};
  width: ${({ itemWidth }) => (itemWidth ? `${itemWidth}px` : 'auto')};

  &:focus {
    background: ${({ theme }) => theme.activeColor};
    color: ${({ theme }) => theme.focusedTextColor};
  }
`;

export const StyledEmptySeasonStub = styled.div`
  height: ${scaleSize(512)}px;
  font-size: ${scaleSize(48)}px;
`;

export const StyledEmptySeasonStubTitle = styled.div`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.mainTextColor};
  text-align: center;
`;

export const StyledRecommendationsList = styled(HorizontalList)`
  height: ${scaleSize(630)}px;
  margin-top: ${scaleSize(5)}px;
  position: relative;
  white-space: nowrap;
`;

export const StyledEpisodesList = styled(HorizontalList)`
  height: ${scaleSize(515)}px;
  position: relative;
  white-space: nowrap;
`;
